import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Menu, LogOut, Search, Settings, KeyRound } from 'lucide-react';
import { useAppStore, useIsStaff } from '@/app/store';
import { openQuickJump } from './QuickJump';

export function TopBar({ onOpenMenu }: { onOpenMenu: () => void }) {
  const user = useAppStore((s) => s.user);
  const companies = useAppStore((s) => s.companies);
  const isStaff = useIsStaff();
  const signOut = useAppStore((s) => s.signOut);
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setMenuOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const isMac = navigator.platform.toUpperCase().includes('MAC');

  return (
    <header className="sticky top-0 z-20 flex h-14 items-center gap-2 border-b border-border bg-surface/80 px-4 backdrop-blur sm:gap-3 lg:px-6">
      <button
        onClick={onOpenMenu}
        className="rounded-md p-2 text-fg-muted hover:bg-surface-2 lg:hidden"
        aria-label="Open menu"
      >
        <Menu className="h-5 w-5" />
      </button>

      <button
        onClick={openQuickJump}
        className="hidden h-9 w-72 items-center gap-2.5 rounded-md border border-border bg-bg px-3 text-sm text-fg-subtle transition hover:border-border-strong hover:text-fg-muted sm:flex"
      >
        <Search className="h-4 w-4" />
        Search
        <kbd className="ml-auto rounded-sm border border-border bg-surface px-1.5 py-0.5 text-2xs">
          {isMac ? '⌘K' : 'Ctrl K'}
        </kbd>
      </button>
      <button
        onClick={openQuickJump}
        className="rounded-md p-2 text-fg-muted hover:bg-surface-2 sm:hidden"
        aria-label="Search"
      >
        <Search className="h-5 w-5" />
      </button>

      <div className="ml-auto flex items-center gap-1">
        <div ref={ref} className="relative">
          <button
            onClick={() => setMenuOpen((o) => !o)}
            className="flex items-center gap-2 rounded-md py-1 pl-1 pr-2 hover:bg-surface-2"
            aria-label="Account menu"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-heading font-display text-sm font-semibold text-white">
              {(user?.name ?? user?.email)?.slice(0, 1).toUpperCase() ?? '?'}
            </span>
          </button>

          {menuOpen && (
            <div className="absolute right-0 z-50 mt-1 w-64 rounded-lg border border-border bg-surface p-1 shadow-pop">
              <div className="px-3 py-2.5">
                <p className="truncate text-sm font-medium text-fg">{user?.name ?? user?.email}</p>
                <p className="truncate text-xs text-fg-muted">{user?.email}</p>
                <p className="mt-1.5 text-xs text-fg-subtle">
                  {isStaff
                    ? 'WarriorLab staff'
                    : companies.length
                      ? `${companies[0]!.role === 'ADMIN' ? 'Admin' : 'Member'} · ${companies.map((c) => c.name).join(', ')}`
                      : 'Client'}
                </p>
              </div>
              <div className="my-1 border-t border-border" />
              <MenuLink to="/settings" icon={Settings} label="Settings" onGo={() => setMenuOpen(false)} />
              {isStaff && (
                <MenuLink to="/settings/claude" icon={KeyRound} label="Platform settings" onGo={() => setMenuOpen(false)} />
              )}
              <div className="my-1 border-t border-border" />
              <button
                onClick={async () => {
                  await signOut();
                  navigate('/login');
                }}
                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-fg-muted hover:bg-surface-2 hover:text-fg"
              >
                <LogOut className="h-4 w-4" />
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

function MenuLink({
  to,
  icon: Icon,
  label,
  onGo,
}: {
  to: string;
  icon: typeof Settings;
  label: string;
  onGo: () => void;
}) {
  return (
    <Link
      to={to}
      onClick={onGo}
      className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-fg-muted hover:bg-surface-2 hover:text-fg"
    >
      <Icon className="h-4 w-4" />
      {label}
    </Link>
  );
}
